/**
 * Per-state tax data for the demo's state-specific content.
 *
 * `rate` is an approximate **effective** state income-tax rate for the demo persona's
 * income level (~$122k net income with $45k in W-2 wages, single filer) — not a marginal
 * rate and not a tax engine. It exists so the demo's Quarterly Estimates page shows a
 * figure that is plausible for the prospect's state instead of always showing
 * California's.
 *
 * ⚠️ Computed, not tax-team reviewed. Still worth a sign-off before these numbers carry a
 * sales conversation — but they are no longer guesses. Each is a one-line edit below.
 *
 * Derivation (2026 tax year, single filer, $122,000):
 *   taxable = 122000 − standard deduction − personal exemption
 *   tax     = taxable run through the state's 2026 bracket schedule, minus any exemption
 *             taken as a CREDIT rather than a deduction (AZ, AR, CA, DE, IA, NE, OR, UT)
 *   rate    = tax / 122000
 * Brackets, deductions and exemptions from the Tax Foundation's 2026 state individual
 * income tax tables: https://taxfoundation.org/data/all/state/state-income-tax-rates-2026/
 *
 * These were previously raw flat/marginal rates with no deduction applied, so nearly every
 * state read high — NC at 4.25% (its pre-2026 marginal) rather than the 3.57% its 3.99%
 * flat rate and $12,750 standard deduction actually produce.
 *
 * Only the RATIO to California matters downstream: `scaleStateTax` normalizes by the anchor,
 * so California's authored dollar figures are unchanged no matter what CA's rate is, and
 * every other state moves relative to it. Consistent methodology across states therefore
 * matters more than absolute precision in any one.
 *
 * Known simplifications:
 *   - Local/county income taxes are excluded (notably MD, NY City, and OH/PA
 *     municipalities), so those states read low.
 *   - The nine no-income-tax states are 0, which suppresses the state row entirely
 *     (see `deriveStateEstimation`). WA is among them: its 7% tax reaches capital gains
 *     only, not the wage and pass-through income this persona has.
 *   - Graduated brackets are collapsed to a single effective rate at the persona's
 *     income; a prospect with very different income would see a less accurate figure.
 *   - A "flat" state can still exempt a band before its rate starts, and the summary tables
 *     do not always show it. Three states here tax nothing below a threshold:
 *       OH — 2.75% applies only ABOVE $26,050 (HB96 completed the flattening for 2026).
 *            Treating it as flat from the first dollar produced 2.70% instead of 2.11%.
 *       MS — first $10,000 of taxable income exempt, then 4% (Build Up Mississippi Act).
 *            Treating it as flat produced 3.73% instead of 3.40%.
 *       ND — first $48,475 exempt, then 1.95%, which is why 0.92% is correct, not a typo.
 *     Before trusting a "flat rate" for a new year, check whether it starts at dollar one.
 *   - A credit this persona cannot claim must NOT be subtracted, which is where the first
 *     pass went wrong on three states:
 *       UT — equals its flat rate (4.45%, cut by SB60 for 2026) exactly. Utah has no
 *            standard deduction; the taxpayer tax credit does that job and phases out at
 *            1.3% of income above ~$17k, so at $122k it is fully consumed and nothing is
 *            subtracted. Subtracting it produced the earlier 3.71%.
 *       OR — the exemption credit is a hard cutoff, not a taper: $0 above $100k AGI for a
 *            single filer, so the persona gets none. Subtracting it produced 8.07%.
 *       AZ — the $100 is a DEPENDENT credit, not a personal exemption. A single filer with
 *            no dependents claims nothing. Subtracting it produced 2.25%.
 *     CA's $153 exemption credit does apply — its phase-out starts far above this income.
 *   - Rates move most years — eight states cut on 2026-01-01 alone. Re-derive when the
 *     Tax Foundation publishes the next table rather than patching states one at a time.
 */

export interface StateTaxInfo {
    /** Display name, used wherever the demo shows the state spelled out. */
    name: string;
    /** Approximate effective state income-tax rate, as a decimal fraction. */
    rate: number;
    /**
     * Representative city + ZIP for the demo's prefilled business/home address. Without
     * these, personalizing only the state code would render addresses like
     * "San Francisco, TX 94103".
     */
    city: string;
    zip: string;
    /** Drives the formation flow's community-property branch (9 states + CA). */
    isCommunityProperty?: boolean;
    /**
     * The state's estimated-payment portal. Omitted where we have no verified URL —
     * `getStatePaymentPortalUrl` falls back rather than guessing one. Never set this for a
     * `rate: 0` state; see that function for why the empty string is load-bearing.
     */
    paymentPortalUrl?: string;
}

export const STATE_TAX: Record<string, StateTaxInfo> = {
    AL: { name: 'Alabama', rate: 0.0478, city: 'Birmingham', zip: '35203' },
    AK: { name: 'Alaska', rate: 0, city: 'Anchorage', zip: '99501' },
    AZ: { name: 'Arizona', rate: 0.0233, city: 'Phoenix', zip: '85004', isCommunityProperty: true },
    AR: { name: 'Arkansas', rate: 0.0373, city: 'Little Rock', zip: '72201' },
    CA: {
        name: 'California',
        rate: 0.0583,
        city: 'San Francisco',
        zip: '94103',
        isCommunityProperty: true,
        // The one URL verified in-repo — the Storybook QTE handler ships the same value.
        paymentPortalUrl: 'https://www.ftb.ca.gov/pay',
    },
    CO: { name: 'Colorado', rate: 0.0382, city: 'Denver', zip: '80202' },
    CT: { name: 'Connecticut', rate: 0.0424, city: 'Hartford', zip: '06103' },
    DC: { name: 'Washington, D.C.', rate: 0.0607, city: 'Washington', zip: '20001' },
    DE: { name: 'Delaware', rate: 0.055, city: 'Wilmington', zip: '19801' },
    FL: { name: 'Florida', rate: 0, city: 'Miami', zip: '33130' },
    GA: { name: 'Georgia', rate: 0.0468, city: 'Atlanta', zip: '30303' },
    HI: { name: 'Hawaii', rate: 0.0635, city: 'Honolulu', zip: '96813' },
    IA: { name: 'Iowa', rate: 0.0327, city: 'Des Moines', zip: '50309' },
    ID: { name: 'Idaho', rate: 0.046, city: 'Boise', zip: '83702', isCommunityProperty: true },
    IL: { name: 'Illinois', rate: 0.0483, city: 'Chicago', zip: '60602' },
    IN: { name: 'Indiana', rate: 0.0293, city: 'Indianapolis', zip: '46204' },
    KS: { name: 'Kansas', rate: 0.0492, city: 'Wichita', zip: '67202' },
    KY: { name: 'Kentucky', rate: 0.034, city: 'Louisville', zip: '40202' },
    LA: { name: 'Louisiana', rate: 0.0268, city: 'New Orleans', zip: '70112', isCommunityProperty: true },
    MA: { name: 'Massachusetts', rate: 0.0482, city: 'Boston', zip: '02108' },
    MD: { name: 'Maryland', rate: 0.0448, city: 'Baltimore', zip: '21201' },
    ME: { name: 'Maine', rate: 0.0592, city: 'Portland', zip: '04101' },
    MI: { name: 'Michigan', rate: 0.0404, city: 'Detroit', zip: '48226' },
    MN: { name: 'Minnesota', rate: 0.0555, city: 'Minneapolis', zip: '55401' },
    MO: { name: 'Missouri', rate: 0.0393, city: 'Kansas City', zip: '64106' },
    MS: { name: 'Mississippi', rate: 0.034, city: 'Jackson', zip: '39201' },
    MT: { name: 'Montana', rate: 0.0453, city: 'Billings', zip: '59101' },
    NC: { name: 'North Carolina', rate: 0.0357, city: 'Charlotte', zip: '28202' },
    ND: { name: 'North Dakota', rate: 0.0092, city: 'Fargo', zip: '58102' },
    NE: { name: 'Nebraska', rate: 0.0383, city: 'Omaha', zip: '68102' },
    NH: { name: 'New Hampshire', rate: 0, city: 'Manchester', zip: '03101' },
    NJ: { name: 'New Jersey', rate: 0.0458, city: 'Newark', zip: '07102' },
    NM: { name: 'New Mexico', rate: 0.0381, city: 'Albuquerque', zip: '87102', isCommunityProperty: true },
    NV: { name: 'Nevada', rate: 0, city: 'Las Vegas', zip: '89101', isCommunityProperty: true },
    NY: { name: 'New York', rate: 0.0505, city: 'New York', zip: '10007' },
    OH: { name: 'Ohio', rate: 0.0211, city: 'Columbus', zip: '43215' },
    OK: { name: 'Oklahoma', rate: 0.0405, city: 'Oklahoma City', zip: '73102' },
    OR: { name: 'Oregon', rate: 0.0828, city: 'Portland', zip: '97204' },
    PA: { name: 'Pennsylvania', rate: 0.0307, city: 'Philadelphia', zip: '19107' },
    RI: { name: 'Rhode Island', rate: 0.0344, city: 'Providence', zip: '02903' },
    SC: { name: 'South Carolina', rate: 0.0505, city: 'Charleston', zip: '29401' },
    SD: { name: 'South Dakota', rate: 0, city: 'Sioux Falls', zip: '57104' },
    TN: { name: 'Tennessee', rate: 0, city: 'Nashville', zip: '37201' },
    TX: { name: 'Texas', rate: 0, city: 'Austin', zip: '78701', isCommunityProperty: true },
    UT: { name: 'Utah', rate: 0.0445, city: 'Salt Lake City', zip: '84111' },
    VA: { name: 'Virginia', rate: 0.0508, city: 'Richmond', zip: '23219' },
    VT: { name: 'Vermont', rate: 0.0458, city: 'Burlington', zip: '05401' },
    WA: { name: 'Washington', rate: 0, city: 'Seattle', zip: '98104', isCommunityProperty: true },
    WI: { name: 'Wisconsin', rate: 0.0417, city: 'Milwaukee', zip: '53202', isCommunityProperty: true },
    WV: { name: 'West Virginia', rate: 0.0405, city: 'Charleston', zip: '25301' },
    WY: { name: 'Wyoming', rate: 0, city: 'Cheyenne', zip: '82001' },
};

/**
 * State the demo falls back to when the token carries none.
 *
 * Tokens minted before `state` existed, and prospects who skipped the funnel question,
 * resolve to this. It is California so those demos render exactly as they did before
 * state personalization shipped — the address and QTE figures were all hardcoded to CA.
 * Flip this to change the un-personalized demo.
 */
export const DEFAULT_DEMO_STATE = 'CA';

/** The QTE figures below are anchored to this state's rate — see `scaleStateTax`. */
const ANCHOR_STATE = 'CA';

/** Resolve a possibly-null/unknown code to one we have data for. */
export function resolveStateCode(state: string | null | undefined): string {
    if (!state) {
        return DEFAULT_DEMO_STATE;
    }
    const normalized = state.trim().toUpperCase();
    return STATE_TAX[normalized] ? normalized : DEFAULT_DEMO_STATE;
}

export function getStateTaxInfo(state: string | null | undefined): StateTaxInfo {
    return STATE_TAX[resolveStateCode(state)];
}

export function getStateName(state: string | null | undefined): string {
    return getStateTaxInfo(state).name;
}

/** True for the nine states with no personal income tax, where the demo hides the state row. */
export function hasNoIncomeTax(state: string | null | undefined): boolean {
    return getStateTaxInfo(state).rate === 0;
}

/**
 * Rescale a California-denominated state tax amount to `state`.
 *
 * The demo's quarterly figures were authored for California, so rather than re-deriving
 * them from income (which would change the numbers the team already signed off on for CA)
 * we scale by the ratio of effective rates. California therefore stays byte-identical and
 * every other state moves proportionally.
 */
export function scaleStateTax(californiaAmount: number, state: string | null | undefined): number {
    const { rate } = getStateTaxInfo(state);
    if (rate === 0) {
        return 0;
    }
    return Math.round((californiaAmount * rate) / STATE_TAX[ANCHOR_STATE].rate);
}

/**
 * Stand-in payment portal for taxing states we have no verified URL for.
 *
 * The FTA's agency directory rather than an invented `tax.<xx>.gov/pay` — guessing a
 * plausible-looking URL per state is what produced this file's rate caveat, and a demo
 * that 404s in front of a prospect is worse than one extra click. Replace per state via
 * `paymentPortalUrl` as the tax team supplies real ones.
 */
const FALLBACK_PAYMENT_PORTAL = 'https://taxadmin.org/state-tax-agencies/';

/**
 * The state's estimated-payment portal, or `''` for a state with no income tax.
 *
 * ⚠️ Load-bearing beyond the link it renders: the QTE dashboard infers "this state
 * levies no income tax" from an EMPTY portal URL (`useQuarterEstimates`'s
 * `noStateIncomeTax` exemption, and `useMemberCircumstances`'s `hasStateIncomeTax`).
 * So a taxing state MUST return something truthy here, or its card reads
 * "<State> doesn't collect state income tax" — which is exactly what CA-5697 reported.
 */
export function getStatePaymentPortalUrl(state: string | null | undefined): string {
    const info = getStateTaxInfo(state);
    if (info.rate === 0) {
        return '';
    }
    return info.paymentPortalUrl ?? FALLBACK_PAYMENT_PORTAL;
}
